<script setup>
import { computed, inject, ref } from 'vue';
import { styleVars } from './shared.js';
import { useSponsorSeed } from '../sponsorOrder.js';
import {
    DEFAULT_SUPPORTER_NOTE, directionsUrl, guideCategories, guidePlaces,
    orderPlaces, placesIn, placeWebsite, telHref, websiteLabel,
} from '../localGuide.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// Empty: a prompt in the editor, nothing on the live site.
const inEditor = inject('tiqEditorCanvas', false);
const seed = useSponsorSeed();
const places = computed(() => orderPlaces(guidePlaces(props.config.items),
    { random: !! props.config.random_order, inEditor, seed: seed.value }));
const categories = computed(() => guideCategories(places.value));
const showChips = computed(() => props.config.show_filters !== false && categories.value.length > 1);
const active = ref('');
const shown = computed(() => placesIn(places.value, showChips.value ? active.value : ''));

const marks = computed(() => props.config.show_supporters !== false);
const anySupporter = computed(() => marks.value && places.value.some((p) => p.supporter));
const note = computed(() => String(props.config.supporter_note ?? DEFAULT_SUPPORTER_NOTE).trim());

// Trailhead's shared colors plus the one this block adds: the cards.
const sectionStyle = computed(() => ({
    ...styleVars(props.config),
    ...(props.config.style?.card_bg ? { '--guide-card': props.config.style.card_bg } : {}),
}));

function links(p) {
    const site = placeWebsite(p.href);
    return {
        directions: directionsUrl(p, props.config.town),
        site,
        siteLabel: site ? websiteLabel(site) : '',
        tel: telHref(p.phone),
    };
}
</script>

<template>
    <section v-if="places.length || inEditor" :id="config.anchor_id || null" class="th-block th-sec th-guide" :style="sectionStyle">
        <div class="th-wrap">
            <header class="th-guide__head">
                <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                <h2 v-if="config.heading" class="th-h2 th-guide__heading">{{ config.heading }}</h2>
                <p v-if="config.intro" class="th-lede th-guide__intro">{{ config.intro }}</p>
            </header>

            <div v-if="showChips" class="th-chips th-guide__chips" role="group" aria-label="Show places by kind">
                <button type="button" class="th-chip th-guide__chip" :class="{ 'is-active': active === '' }"
                        :aria-pressed="active === '' ? 'true' : 'false'" @click="active = ''">All</button>
                <button v-for="c in categories" :key="c" type="button" class="th-chip th-guide__chip"
                        :class="{ 'is-active': active === c }"
                        :aria-pressed="active === c ? 'true' : 'false'" @click="active = c">{{ c }}</button>
            </div>

            <ul v-if="shown.length" class="th-guide__grid">
                <li v-for="(p, i) in shown" :key="`${p.name}-${i}`" class="th-panel th-guide__card">
                    <div v-if="p.photo" class="th-guide__photo">
                        <img :src="p.photo" alt="" loading="lazy">
                    </div>
                    <div class="th-guide__body">
                        <p class="th-guide__kind">
                            <span>{{ p.category }}</span>
                            <span v-if="marks && p.supporter" class="th-guide__heart" title="Supports our trails">
                                <span aria-hidden="true">♥</span><span class="th-guide__sr">Supports our trails</span>
                            </span>
                        </p>
                        <h3 class="th-h3 th-guide__name">{{ p.name }}</h3>
                        <p v-if="p.blurb" class="th-small th-guide__blurb">{{ p.blurb }}</p>
                        <p v-if="p.perk" class="th-guide__perk"><strong>Trail perk</strong> {{ p.perk }}</p>
                        <dl v-if="p.address || p.hours" class="th-guide__meta">
                            <template v-if="p.address"><dt>Where</dt><dd>{{ p.address }}</dd></template>
                            <template v-if="p.hours"><dt>Hours</dt><dd>{{ p.hours }}</dd></template>
                        </dl>
                        <div v-if="links(p).directions || links(p).site || links(p).tel" class="th-guide__actions">
                            <a v-if="links(p).directions" :href="links(p).directions" target="_blank" rel="noopener">Directions</a>
                            <a v-if="links(p).site" :href="links(p).site" target="_blank" rel="noopener">{{ links(p).siteLabel || 'Website' }}</a>
                            <a v-if="links(p).tel" :href="links(p).tel">{{ p.phone }}</a>
                        </div>
                    </div>
                </li>
            </ul>
            <p v-else class="th-small th-guide__empty">Add places in the editor: coffee on the way out, the bike shop, somewhere to eat after.</p>

            <p v-if="anySupporter && note" class="th-small th-guide__note">{{ note }}</p>
        </div>
    </section>
</template>

<style scoped>
.th-guide__head { max-width: 760px; margin: 0 0 28px; }
.th-guide__intro { margin-top: 14px; }
.th-guide__chips { margin: 0 0 24px; }
.th-guide__grid {
    /* A card with a photo is twice as tall as one without; stretching
       its row-mates to match leaves a hole in the middle of them. */
    align-items: start;
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
    gap: 16px;
}
.th-guide__card { display: flex; flex-direction: column; background: var(--guide-card, #fff); }
.th-guide__photo { aspect-ratio: 16 / 10; background: var(--th-line-soft); }
.th-guide__photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.th-guide__body { padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
.th-guide__kind {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0;
    font: 500 12px/1.2 var(--th-mono);
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--th-meta);
}
.th-guide__heart { color: var(--accent); font-size: 16px; letter-spacing: 0; }
.th-guide__sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.th-guide__perk {
    margin: 0;
    padding: 8px 10px;
    border-left: 3px solid var(--accent);
    background: var(--tint);
    font-size: 14px;
    line-height: 1.4;
}
.th-guide__perk strong { display: block; font: 500 11px/1.4 var(--th-mono); letter-spacing: .1em; text-transform: uppercase; color: var(--th-meta); }
.th-guide__meta { display: grid; grid-template-columns: auto 1fr; gap: 2px 10px; margin: 0; font-size: 14px; }
.th-guide__meta dt { color: var(--th-faint); }
.th-guide__meta dd { margin: 0; color: var(--th-muted); overflow-wrap: anywhere; }
.th-guide__actions { display: flex; flex-wrap: wrap; gap: 6px 16px; margin-top: auto; padding-top: 6px; font-size: 14px; font-weight: 600; }
.th-guide__actions a { color: var(--th-ink); text-decoration: underline; text-decoration-color: var(--accent); text-underline-offset: 3px; overflow-wrap: anywhere; }
.th-guide__note { margin-top: 24px; }
.th-guide__empty { font-style: italic; }
</style>
